/* eslint-disable */
import { useState, useEffect, useRef, useContext, Fragment } from 'react';

import * as Yup from 'yup';
import { Formik, FieldArray, Form, Field } from 'formik';
import { toast } from 'react-toastify';
import toastError from '../../toasts/toastError';

import {
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  IconButton,
  InputAdornment,
  Button,
  TextField,
  Dialog,
  DialogContent,
  DialogActions,
  DialogTitle,
  CircularProgress,
  Switch,
  createFilterOptions,
  useTheme,
  Stepper,
  Step,
  StepLabel,
  Checkbox,
  Typography,
} from '@mui/material';

import { Save, Edit, HelpOutlineOutlined } from '@mui/icons-material';
import { i18n } from '../../translate/i18n';

import api from '../../services/api';

import { Colorize, DeleteOutline } from '@mui/icons-material';

import ConfirmationModal from '../ConfirmationModal';
import CustomToolTip from '../ToolTips';

import { AuthContext } from '../../context/Auth/AuthContext';
import useQueues from '../../hooks/useQueues';
import ColorBoxModal from '../ColorBoxModal';
import { getStyled } from './styles';

const QueueSchema = Yup.object().shape({
  name: Yup.string()
    .min(2, 'Too Short!')
    .max(50, 'Too Long!')
    .required('Required'),
  color: Yup.string().min(3, 'Too Short!').max(9, 'Too Long!').required(),
  greetingMessage: Yup.string(),
  chatbots: Yup.array()
    .of(
      Yup.object().shape({
        name: Yup.string().min(4, 'too short').required('Required'),
      }),
    )
    .required('Must have friends'),
});

const QueueModal = ({ open, onClose, queueId, onEdit }) => {
  const theme = useTheme();
  const classes = getStyled(theme);

  const initialState = {
    name: '',
    color: '',
    greetingMessage: '',
    chatbots: [],
    outOfHoursMessage: '',
    orderQueue: '',
    tempoRoteador: 0,
    ativarRoteador: false,
    fileListId: '',
    closeTicket: false,
  };

  const [colorPickerModalOpen, setColorPickerModalOpen] = useState(false);
  const [queue, setQueue] = useState(initialState);
  const greetingRef = useRef();
  const [activeStep, setActiveStep] = useState(null);
  const [selectedQueue, setSelectedQueue] = useState(null);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [isStepContent, setIsStepContent] = useState(true);
  const [isNameEdit, setIsNamedEdit] = useState(null);
  const [isGreetingMessageEdit, setGreetingMessageEdit] = useState(null);
  const [queues, setQueues] = useState([]);

  const [tab, setTab] = useState(0);
  const [file, setFile] = useState(null);
  const { user } = useContext(AuthContext);
  const [searchParam, setSearchParam] = useState('');
  const [loading, setLoading] = useState(false);

  const [selectedQueueOption, setSelectedQueueOption] = useState('');
  const { findAll: findAllQueues } = useQueues();
  const [allQueues, setAllQueues] = useState([]);
  const [userOptions, setUserOptions] = useState([]);
  const isMounted = useRef(true);
  const companyId = user.companyId;

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get('/files/', {
          params: { companyId },
        });

        setFile(data.files);
      } catch (err) {
        toastError(err);
      }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      if (!queueId) return;
      try {
        const { data } = await api.get(`/queue/${queueId}`);

        setQueue((prevState) => ({
          ...prevState,
          ...data,
          name: data.name ?? '',
          color: data.color ?? '',
          greetingMessage: data.greetingMessage ?? '',
          chatbots: data.chatbots ?? [],
          outOfHoursMessage: data.outOfHoursMessage ?? '',
          orderQueue: data.orderQueue ?? '',
          tempoRoteador: data.tempoRoteador ?? 0,
          ativarRoteador: data.ativarRoteador ?? false,
          fileListId: data.fileListId ?? '',
          closeTicket: data.closeTicket ?? false,
        }));
      } catch (err) {
        toastError(err);
      }
    })();

    return () => {
      setQueue(initialState);
    };
  }, [queueId, open]);

  useEffect(() => {
    if (isMounted.current) {
      const loadQueues = async () => {
        const list = await findAllQueues();
        setAllQueues(list);
        setQueues(list);
      };
      loadQueues();
    }
  }, []);

  useEffect(() => {
    if (searchParam.length < 3) {
      setLoading(false);
      setSelectedQueueOption('');
      return;
    }
    const delayDebounceFn = setTimeout(() => {
      const fetchUsers = async () => {
        try {
          const { data } = await api.get('/users/');
          setUserOptions(data.users);
          setLoading(false);
        } catch (err) {
          setLoading(false);
          toastError(err);
        }
      };

      fetchUsers();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [searchParam]);

  useEffect(() => {
    if (activeStep) {
      setSelectedQueueOption(queue.chatbots[activeStep]?.optQueueId);
    }

    if (activeStep === isNameEdit) {
      setIsStepContent(false);
    } else {
      setIsStepContent(true);
    }
  }, [isNameEdit, activeStep]);

  const handleClose = () => {
    onClose();
    setIsNamedEdit(null);
    setActiveStep(null);
    setGreetingMessageEdit(null);
  };

  const handleCloseConfirmationModal = () => {
    setConfirmModalOpen(false);
    setSelectedQueue(null);
  };

  const handleDeleteQueue = async (optionsId) => {
    try {
      await api.delete(`/chatbot/${optionsId}`);
      const { data } = await api.get(`/queue/${queueId}`);
      setQueue(initialState);
      setQueue(data);

      setIsNamedEdit(null);
      setGreetingMessageEdit(null);
      toastSuccess(`${i18n.t('queues.toasts.deleted')}`);
    } catch (err) {
      toastError('Erro ao deletar fila!', err);
    }
  };

  const handleSaveQueue = async (values) => {
    try {
      if (queueId) {
        await api.put(`/queue/${queueId}`, { ...values /* , schedules */ });
      } else {
        await api.post('/queue', { ...values /* , schedules */ });
      }

      toastSuccess(`${i18n.t('queues.toasts.success')}`);
      handleClose();
    } catch (err) {
      toastError('Erro ao criar fila!', err);
    }
  };

  const handleSaveBot = async (values) => {
    try {
      if (queueId) {
        const { data } = await api.put(`/queue/${queueId}`, values);
        if (data.chatbots && data.chatbots.length) {
          onEdit(data);
          setQueue(data);
        }
      } else {
        const { data } = await api.post('/queue', values);
        if (data.chatbots && data.chatbots.length) {
          setQueue(data);
          onEdit(data);
          handleClose();
        }
      }

      setIsNamedEdit(null);
      setGreetingMessageEdit(null);
      toastSuccess(`${i18n.t('queues.toasts.success')}`);
    } catch (err) {
      toastError(err);
    }
  };

  return (
    <div style={classes.root}>
      <ConfirmationModal
        title={
          selectedQueue &&
          `${i18n.t('queues.confirmationModal.deleteTitle')} ${
            selectedQueue.name
          }?`
        }
        open={confirmModalOpen}
        onClose={handleCloseConfirmationModal}
        onConfirm={() => handleDeleteQueue(selectedQueue.id)}
      >
        {i18n.t('queueModal.title.confirmationDelete')}
      </ConfirmationModal>
      <Dialog
        maxWidth='md'
        fullWidth
        open={open}
        onClose={handleClose}
        scroll='paper'
      >
        <DialogTitle sx={theme.custom.dialogColor}>
          {queueId
            ? `${i18n.t('queueModal.title.edit')}`
            : `${i18n.t('queueModal.title.add')}`}
        </DialogTitle>
        {tab === 0 && (
          <Formik
            initialValues={queue}
            validateOnChange={false}
            enableReinitialize={true}
            validationSchema={QueueSchema}
            onSubmit={(values, actions) => {
              setTimeout(() => {
                handleSaveQueue(values);
                actions.setSubmitting(false);
              }, 400);
            }}
          >
            {({ setFieldValue, touched, errors, isSubmitting, values }) => (
              <Form>
                <DialogContent dividers sx={theme.custom.dialogColor}>
                  <div
                    style={{
                      display: 'flex',
                    }}
                  >
                    <Field
                      as={TextField}
                      label={i18n.t('queueModal.form.name')}
                      autoFocus
                      name='name'
                      error={touched.name && Boolean(errors.name)}
                      helperText={touched.name && errors.name}
                      variant='outlined'
                      margin='dense'
                      sx={theme.custom.textField}
                      fullWidth
                    />
                    <Field
                      as={TextField}
                      sx={theme.custom.textField}
                      label={i18n.t('queueModal.form.color')}
                      name='color'
                      id='color'
                      onFocus={() => {
                        setColorPickerModalOpen(true);
                        greetingRef.current.focus();
                      }}
                      error={touched.color && Boolean(errors.color)}
                      helperText={touched.color && errors.color}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position='start'>
                            <div
                              style={{
                                ...classes.colorAdorment,
                                backgroundColor: values.color,
                              }}
                            ></div>
                          </InputAdornment>
                        ),
                        endAdornment: (
                          <IconButton
                            size='small'
                            sx={{ color: theme.custom.colors.text }}
                            onClick={() =>
                              setColorPickerModalOpen(!colorPickerModalOpen)
                            }
                          >
                            <Colorize />
                          </IconButton>
                        ),
                      }}
                      variant='outlined'
                      margin='dense'
                      fullWidth
                    />
                    <ColorBoxModal
                      open={colorPickerModalOpen}
                      handleClose={() => setColorPickerModalOpen(false)}
                      onChange={(color) => {
                        setFieldValue('color', color);
                      }}
                      currentColor={values.color}
                    />

                    <FormControlLabel
                      control={
                        <Field
                          as={Switch}
                          name='closeTicket'
                          checked={values.closeTicket}
                          sx={theme.custom.switch}
                        />
                      }
                      label={i18n.t('queueModal.form.closeTicket')}
                    />
                  </div>

                  <div>
                    <FormControl
                      variant='outlined'
                      margin='dense'
                      sx={classes.FormControl}
                      fullWidth
                    >
                      <InputLabel
                        id='fileListId-selection-label'
                        sx={theme.custom.label}
                      >
                        {i18n.t('queueModal.form.fileListId')}
                      </InputLabel>
                      <Field
                        as={Select}
                        label={i18n.t('queueModal.form.fileListId')}
                        name='fileListId'
                        id='fileListId'
                        placeholder={i18n.t('queueModal.form.fileListId')}
                        labelId='fileListId-selection-label'
                        value={values.fileListId || ''}
                        sx={theme.custom.textField}
                        MenuProps={theme.custom.menuProps}
                      >
                        <MenuItem value={''}>{'Nenhum'}</MenuItem>
                        {file.map((f) => (
                          <MenuItem key={f.id} value={f.id}>
                            {f.name}
                          </MenuItem>
                        ))}
                      </Field>
                    </FormControl>
                  </div>
                  <div>
                    <Field
                      as={TextField}
                      label={i18n.t('queueModal.form.greetingMessage')}
                      type='greetingMessage'
                      multiline
                      inputRef={greetingRef}
                      minRows={5}
                      name='greetingMessage'
                      error={
                        touched.greetingMessage &&
                        Boolean(errors.greetingMessage)
                      }
                      helperText={
                        touched.greetingMessage && errors.greetingMessage
                      }
                      variant='outlined'
                      margin='dense'
                      sx={{ ...theme.custom.textField, width: '98.2%' }}
                    />
                  </div>

                  {/* <Typography variant='subtitle1'>
                    {i18n.t('queueModal.bot.title')}
                    <CustomToolTip
                      title={i18n.t('queueModal.bot.toolTipTitle')}
                      content={i18n.t('queueModal.bot.toolTip')}
                    >
                      <HelpOutlineOutlined
                        style={{ marginLeft: '14px' }}
                        fontSize='small'
                      />
                    </CustomToolTip>
                  </Typography>

                  <div>
                    <FieldArray name='chatbots'>
                      {({ push, remove }) => (
                        <>
                          <Stepper
                            nonLinear
                            activeStep={activeStep}
                            orientation='vertical'
                          >
                            {values.chatbots &&
                              values.chatbots.length > 0 &&
                              values.chatbots.map((info, index) => (
                                <Step
                                  key={`${info.id ? info.id : index}-chatbots`}
                                  onClick={() => setActiveStep(index)}
                                >
                                  <StepLabel key={`${info.id}-chatbots`}>
                                    {isNameEdit !== index &&
                                    queue.chatbots[index]?.name ? (
                                      <div
                                        style={classes.greetingMessage}
                                        variant='body1'
                                      >
                                        {values.chatbots[index].name}

                                        <IconButton
                                          size='small'
                                          onClick={() => {
                                            setIsNamedEdit(index);
                                            setIsStepContent(false);
                                          }}
                                        >
                                          <Edit />
                                        </IconButton>

                                        <IconButton
                                          onClick={() => {
                                            setSelectedQueue(info);
                                            setConfirmModalOpen(true);
                                          }}
                                          size='small'
                                        >
                                          <DeleteOutline />
                                        </IconButton>
                                      </div>
                                    ) : (
                                      <Grid spacing={2} container>
                                        <Grid xs={12} md={12} item>
                                          <Field
                                            as={TextField}
                                            name={`chatbots[${index}].name`}
                                            variant='outlined'
                                            margin='dense'
                                            color='primary'
                                            label={i18n.t(
                                              'queueModal.form.greetingMessage',
                                            )}
                                            disabled={isSubmitting}
                                            autoFocus
                                            fullWidth
                                            size='small'
                                            error={
                                              touched?.chatbots?.[index]
                                                ?.name &&
                                              Boolean(
                                                errors.chatbots?.[index]?.name,
                                              )
                                            }
                                            sx={theme.custom.textField}
                                          />
                                        </Grid>
                                        <Grid xs={12} md={8} item>
                                          <FormControl
                                            variant='outlined'
                                            margin='dense'
                                            sx={classes.formControl}
                                            fullWidth
                                          >
                                            <InputLabel id='queueType-selection-label'>
                                              {i18n.t(
                                                'queueModal.form.queueType',
                                              )}
                                            </InputLabel>

                                            <Field
                                              as={Select}
                                              name={`chatbots[${index}].queueType`}
                                              variant='outlined'
                                              margin='dense'
                                              fullWidth
                                              labelId='queueType-selection-label'
                                              label={i18n.t(
                                                'queueModal.form.queueType',
                                              )}
                                              error={
                                                touched?.chatbots?.[index]
                                                  ?.queueType &&
                                                Boolean(
                                                  errors?.chatbots?.[index]
                                                    ?.queueType,
                                                )
                                              }
                                            >
                                              <MenuItem value={'text'}>
                                                {i18n.t('queueModal.bot.text')}
                                              </MenuItem>
                                              <MenuItem value={'attendent'}>
                                                {i18n.t(
                                                  'queueModal.bot.attendent',
                                                )}
                                              </MenuItem>
                                              <MenuItem value={'queue'}>
                                                {i18n.t('queueModal.bot.queue')}
                                              </MenuItem>
                                              <MenuItem value={'file'}>
                                                {i18n.t('queueModal.bot.file')}
                                              </MenuItem>
                                            </Field>
                                          </FormControl>
                                        </Grid>

                                        <Grid xs={12} md={4} item>
                                          <FormControlLabel
                                            control={
                                              <Field
                                                as={Checkbox}
                                                color='primary'
                                                name={`chatbots[${index}].closeTicket`}
                                                checked={
                                                  values.chatbots[index]
                                                    .closeTicket || false
                                                }
                                              />
                                            }
                                            labelPlacement='top'
                                            label={i18n.t(
                                              'queueModal.form.closeTicket',
                                            )}
                                          />

                                          <IconButton
                                            size='small'
                                            onClick={() =>
                                              values.chatbots[index].name
                                                ? handleSaveBot(values)
                                                : null
                                            }
                                            disabled={isSubmitting}
                                          >
                                            <Save />
                                          </IconButton>

                                          <IconButton
                                            size='small'
                                            onClick={() => remove(index)}
                                            disabled={isSubmitting}
                                          >
                                            <DeleteOutline />
                                          </IconButton>
                                        </Grid>
                                      </Grid>
                                    )}
                                  </StepLabel>
                                </Step>
                              ))}

                            <Step>
                              <StepLabel
                                onClick={() => push({ name: '', value: '' })}
                              >
                                {i18n.t('queueModal.bot.addOptions')}
                              </StepLabel>
                            </Step>
                          </Stepper>
                        </>
                      )}
                    </FieldArray>
                  </div> */}
                </DialogContent>
                <DialogActions sx={theme.custom.dialogColor}>
                  <Button
                    onClick={handleClose}
                    disabled={isSubmitting}
                    variant='outlined'
                    sx={theme.custom.cancelButton}
                  >
                    {i18n.t('queueModal.buttons.cancel')}
                  </Button>
                  <Button
                    type='submit'
                    disabled={isSubmitting}
                    variant='outlined'
                    sx={theme.custom.confirmButton}
                  >
                    {queueId
                      ? `${i18n.t('queueModal.buttons.okEdit')}`
                      : `${i18n.t('queueModal.buttons.okAdd')}`}
                    {isSubmitting && (
                      <CircularProgress size={24} sx={classes.buttonProgress} />
                    )}
                  </Button>
                </DialogActions>
              </Form>
            )}
          </Formik>
        )}
      </Dialog>
    </div>
  );
};

export default QueueModal;
